import Box from "@material-ui/core/Box";
import React from "react";
import { FixedSizeList, ListChildComponentProps, ReactElementType } from "react-window";
import { useWindowSize } from "../../hook";
import { PlainFunctionComponent } from "../../types";
import { ifDefined } from "../../util";
import { addNonProdDisplayName } from "./context/helper";

const SELECTED_OPTION_HEIGHT = 32;
const LIST_COMPONENT_PADDING = 8;
const OVERSCAN_COUNT = 4;

/**
 * This component is designed for use together with the `Autocomplete`
 * to be able to render huge numbers of options.
 *
 * It makes the following assumptions about the rows rendered by `Autocomplete`
 * <ul>
 *     <li>The overall height of a selectable option is 32px</li>
 *     <li>The top and bottom padding of the ListBox component is 8px each</li>
 * </ul>
 */
export const VirtualizedListBox: PlainFunctionComponent<React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>> = React.forwardRef((props, ref) => {
    const { children: childrenProp, ...propsFromAutocomplete } = props;
    const children = React.Children.toArray(childrenProp);
    const itemCount = children.length;

    const { height: windowHeight } = useWindowSize();

    const childrenHeight = SELECTED_OPTION_HEIGHT * itemCount + 2 * LIST_COMPONENT_PADDING;
    const maxBoxHeight = Math.min(childrenHeight, windowHeight);

    // ATTENTION: use `AutocompletePropsContext` in order to transport always changing object `propsFromAutocomplete` down to `FixedSizeListOuterElement` without re-rending
    return (
        <AutocompletePropsContext.Provider value={propsFromAutocomplete}>
            <FixedSizeList
                outerRef={ref}
                overscanCount={OVERSCAN_COUNT}
                itemData={children}
                itemSize={SELECTED_OPTION_HEIGHT}
                width="100%"
                height={maxBoxHeight}
                itemCount={itemCount}
                outerElementType={FixedSizeListOuterElement}
            >
                {ListItem}
            </FixedSizeList>
        </AutocompletePropsContext.Provider>
    );
});

const AutocompletePropsContext = React.createContext<unknown>(undefined); // Note: internal component needs no default, see https://reactjs.org/docs/context.html#reactcreatecontext
addNonProdDisplayName(AutocompletePropsContext, "AutocompletePropsContextHook");

const useAutocompleteProps: () => unknown = () => React.useContext(AutocompletePropsContext);

// This is needed to attach styling and scroll/click handlers from Autocomplete into the windowed list.
const FixedSizeListOuterElement: ReactElementType = React.forwardRef((stylesFromFixedSizedList, ref) => {
    const propsFromAutocomplete = useAutocompleteProps();
    return <Box ref={ref} {...stylesFromFixedSizedList} {...propsFromAutocomplete} />;
});

const ListItem: PlainFunctionComponent<ListChildComponentProps> = ({ data: renderedOptions, index, style }) =>
    ifDefined(
        renderedOptions[index],
        (option) =>
            React.cloneElement(option, {
                style: {
                    ...style,
                    top: ifDefined(style.top, (top) => (top as number) + LIST_COMPONENT_PADDING),
                },
            }),
        null,
    );
